import {
  formatDocumentAddress,
  SEGMENT_SEPARATOR,
  VERSION_SEPARATOR,
  type DocumentAddress,
} from "../references/contentReferences";
import { LIBRARY_TAB_PARAMETER, type LibraryTab } from "../../features/workflows/library/libraryTabs";

/**
 * Every route of the web app, and the only code that builds or reads their addresses
 * (UX-1). Each item kind has its own route; query parameters hold view state only
 * (display mode, a pane beside, a tab), never what is being looked at.
 *
 * Versioned items use the server's reference form `<id>@<version>` in one path segment,
 * and a segment of a document is the URL fragment, so the address reads as the server's
 * segment reference `<id>@<version>#<segment path>`.
 *
 * The project is chosen in the shell and not part of addresses yet (Q-37 is open). Document
 * references are unique across projects, so document addresses need none.
 *
 * Action handoffs also use query parameters: pairing takes `?user_code=` from the
 * connector (RFC 8628), and Workflow edit mode consumes `?add=<exact item version>`
 * into its local draft and `?step=<step key>` by opening that step's form. The Workflow
 * being edited remains in the path.
 */
export const ROUTE_PATTERNS = {
  newTab: "/new-tab",
  landing: "/",
  login: "/login",
  firstAdministratorSetup: "/setup",
  invitationAcceptance: "/invitation",
  verifyEmail: "/verify",
  waitingApproval: "/waiting-for-approval",
  forgotPassword: "/forgot",
  resetPassword: "/reset",
  register: "/register",
  profile: "/profile",
  profilePreferences: "/profile/preferences",
  profileConnectors: "/profile/connectors",
  profileMemories: "/profile/memories",
  connectorPairing: "/connectors/pair",
  /** Where an outside service such as GitHub sends the browser back after the person
   * authorized DMW (ADR-0035); registered with the provider, so it never changes. */
  connectionCallback: "/connections/:provider/callback",
  connectorSetup: "/get-started",
  administrationAccounts: "/administration/accounts",
  administrationServerKeys: "/administration/server-keys",
  administrationInstallation: "/administration/installation",
  administrationResearchSpace: "/administration/researchspace",
  administrationGroups: "/administration/groups",
  administrationGroup: "/administration/groups/:groupId",
  administrationDeletedProjects: "/administration/deleted-projects",
  projects: "/projects",
  projectSettings: "/projects/:projectId/settings",
  projectSettingsAccess: "/projects/:projectId/settings/access",
  projectSettingsGroup: "/projects/:projectId/settings/access/groups/:groupId",
  projectSettingsRules: "/projects/:projectId/settings/rules",
  projectSettingsHistory: "/projects/:projectId/settings/history",
  projectSettingsGeneral: "/projects/:projectId/settings/general",
  /** The chosen project's issues (W-08b). */
  projectIssues: "/issues",
  /** The Documents section (web-app.md WA-7); the code keeps its older name "sources". */
  sources: "/documents",
  sourcesImport: "/documents/import",
  sourceDocument: "/documents/:documentAddress",
  /** Addresses from before the section was renamed, `/sources` itself included; they
   * redirect to Documents. */
  legacySources: "/sources/*",
  /** The Data model section (F19, WA-10); the code keeps its older name "ontology" (I-10).
   * One navigation level (F77): the terms at the section's own address and a version below
   * it, and fixed segments for the pages its header opens, which the router prefers to a
   * version. */
  ontology: "/data-model",
  ontologyVersion: "/data-model/:ontologyVersion",
  ontologyTerm: "/data-model/:ontologyVersion/terms/:termIri",
  /** One extraction result, below the old Instances address (H-21: the list went; the bare
   * address opens the review inbox), so links shared before keep working (NFR-16). */
  instances: "/data-model/instances",
  extraction: "/data-model/instances/:threadId/:entry",
  /** The Checks page went: a version's checks are its status in the Data model page's header
   * (design pass section 9); old addresses open the Data model. */
  formerChecks: "/data-model/checks/*",
  /** The regests prototype's addresses: they open Documents, where a project presents a document (P-07). */
  legacyRegests: "/data-model/regests/*",
  /** The Releases page went: the header opens the releases as a list (design pass section 9). */
  formerReleases: "/data-model/releases",
  release: "/data-model/releases/:datasetId",
  /** Addresses from before the section was renamed, `/ontology` itself included; they
   * redirect to the Data model. */
  legacyOntology: "/ontology/*",
  /** Addresses of the Data section, `/data` itself included; they redirect to a result or the review inbox. */
  legacyData: "/data/*",
  memories: "/memories",
  threads: "/threads",
  /** The Thread view in preparation: work about to start (web-app.md WA-11). */
  threadPreparation: "/threads/new",
  thread: "/threads/:threadId",
  /** A Run's live graph (WD-4), beside its Run view. */
  runGraph: "/threads/:threadId/graph",
  threadHostOffer: "/threads/:threadId/host-offers/:offerId",
  batch: "/batches/:batchId",
  batchVita: "/batches/:batchId/vitae/:vita",
  workflows: "/workflows",
  newStep: "/workflows/new/step",
  newArtifact: "/workflows/new/artifact",
  newSkill: "/workflows/new/skill",
  newLabelList: "/workflows/new/label-list",
  newCapabilityOverrides: "/workflows/new/capability-overrides",
  workflow: "/workflows/:workflowAddress",
  reviews: "/reviews",
  review: "/reviews/:changeSetId",
  importProposal: "/import-proposals/:changeSetId",
  item: "/items/:itemReference",
  /** An item form's old address: it opens the edit's preparation, by hand (CP-06). */
  itemEdit: "/items/:itemReference/edit",
  /** "Report a problem" (F13): opened beside the page it is about, never listed in a navigation. */
  issueReport: "/report-a-problem",
  /** The help center (web-app.md WA-6), open without signing in; a section is the fragment. */
  help: "/help",
  helpPage: "/help/:page",
  /** The static guide's old addresses; they redirect to the help. */
  legacyGuide: "/guide/*",
} as const;

/** Where the app opens after sign-in, and for `/` once signed in. */
export const HOME_PATH = ROUTE_PATTERNS.sources;

/** An exact item version to add to the Workflow editor's draft, on edit mode's old address (`?mode=edit&add=`), which opens the edit's preparation. */
export const WORKFLOW_ADD_PARAMETER = "add";
/** The Workflow view's old address of a Run's graph (F103), which now opens the run graph (WD-4). */
export const WORKFLOW_THREAD_PARAMETER = "thread";

/** A Thread's step to show in its current-step area: a review's owning step (ADR-0031 D4). */
export const THREAD_STEP_PARAMETER = "step";

/** The preparation view's intent, as JSON (WA-11); built and read by `features/threads/prepare/` only. */
export const PREPARATION_INTENT_PARAMETER = "intent";

/** The idempotency key of the preparation's Start, made once per `threads.prepare` call. */
export const PREPARATION_REQUEST_PARAMETER = "request";

/** The request reference a problem report starts from. */
export const ISSUE_REPORT_REFERENCE_PARAMETER = "reference";

/** The query parameter of the pairing page that carries the user code. */
export const PAIRING_USER_CODE_PARAMETER = "user_code";

/** The person whose access panel is open on a project's Access tab. */
export const ACCESS_PERSON_PARAMETER = "person";

/** Location state passed to the sign-in page, so it can return to where the user was. */
export interface ReturnAfterSignIn {
  readonly returnTo: string;
}

export interface OntologyLocation {
  readonly version: string;
  readonly termIri?: string | null;
}

function projectSettingsPath(projectId: string): string {
  return `${ROUTE_PATTERNS.projects}/${encodePathSegment(projectId)}/settings`;
}

export const appPaths = {
  login: () => ROUTE_PATTERNS.login,
  waitingApproval: () => ROUTE_PATTERNS.waitingApproval,
  firstAdministratorSetup: () => ROUTE_PATTERNS.firstAdministratorSetup,
  invitationAcceptance: () => ROUTE_PATTERNS.invitationAcceptance,
  forgotPassword: () => ROUTE_PATTERNS.forgotPassword,
  register: () => ROUTE_PATTERNS.register,
  profile: () => ROUTE_PATTERNS.profile,
  profileConnectors: () => ROUTE_PATTERNS.profileConnectors,
  /** "Personal preferences": language, theme and view defaults (F14), a profile page of its own (F79). */
  profilePreferences: () => ROUTE_PATTERNS.profilePreferences,
  /** Administrators: inviting to DMW and registrations waiting for approval. */
  administrationAccounts: () => ROUTE_PATTERNS.administrationAccounts,
  /** Administrators: the API keys the server holds and who may use them (ADR-0023). */
  administrationServerKeys: () => ROUTE_PATTERNS.administrationServerKeys,
  /** Administrators: what members may do and the rules new projects start with (ST-1). */
  administrationInstallation: () => ROUTE_PATTERNS.administrationInstallation,
  administrationResearchSpace: () => ROUTE_PATTERNS.administrationResearchSpace,
  /** The installation's groups, first on top (accounts.md GS-1). */
  administrationGroups: () => ROUTE_PATTERNS.administrationGroups,
  /** One installation group on its own screen. */
  administrationGroup: (groupId: string) => `${ROUTE_PATTERNS.administrationGroups}/${encodePathSegment(groupId)}`,
  /** Administrators: deleted projects to restore or purge (F121). */
  administrationDeletedProjects: () => ROUTE_PATTERNS.administrationDeletedProjects,
  /** The signed-in user's personal memories (memory.md MEM-7). */
  profileMemories: () => ROUTE_PATTERNS.profileMemories,
  connectorSetup: () => ROUTE_PATTERNS.connectorSetup,
  /** The pairing page, with the code to look up if there is one. */
  connectorPairing: (userCode: string | null = null) =>
    userCode === null
      ? ROUTE_PATTERNS.connectorPairing
      : `${ROUTE_PATTERNS.connectorPairing}?${new URLSearchParams({ [PAIRING_USER_CODE_PARAMETER]: userCode }).toString()}`,
  projects: () => ROUTE_PATTERNS.projects,
  /** A help page, and optionally one of its sections as the fragment (WA-6). */
  help: (page: string, section: string | null = null) =>
    `${ROUTE_PATTERNS.help}/${encodePathSegment(page)}${section === null ? "" : `#${section}`}`,
  /** One project's settings (web-app.md WA-5); it opens on Access. */
  projectSettings: (projectId: string) => projectSettingsPath(projectId),
  /** The project's groups and people; with a person, their access panel open. */
  projectSettingsAccess: (projectId: string, personId: string | null = null) =>
    `${projectSettingsPath(projectId)}/access${personId === null ? "" : `?${new URLSearchParams({ [ACCESS_PERSON_PARAMETER]: personId }).toString()}`}`,
  /** One of the project's groups, or an installation group it includes, on its own screen. */
  projectSettingsGroup: (projectId: string, groupId: string) => `${projectSettingsPath(projectId)}/access/groups/${encodePathSegment(groupId)}`,
  projectSettingsRules: (projectId: string) => `${projectSettingsPath(projectId)}/rules`,
  projectSettingsHistory: (projectId: string) => `${projectSettingsPath(projectId)}/history`,
  projectSettingsGeneral: (projectId: string) => `${projectSettingsPath(projectId)}/general`,
  /** The chosen project's issues; on the DMW project, the problem reports (W-08b). */
  projectIssues: () => ROUTE_PATTERNS.projectIssues,
  sources: () => ROUTE_PATTERNS.sources,
  sourcesImport: () => ROUTE_PATTERNS.sourcesImport,
  /** A document, and optionally one of its segments as the fragment. */
  sourceDocument: (address: DocumentAddress, segmentPath: string | null = null) => {
    const documentPath = `${ROUTE_PATTERNS.sources}/${encodePathSegment(formatDocumentAddress(address))}`;
    return segmentPath === null ? documentPath : `${documentPath}${SEGMENT_SEPARATOR}${encodeURI(segmentPath)}`;
  },
  /** The Data model's terms, at the latest version or at `location`. */
  ontology: (location?: OntologyLocation) => {
    if (location === undefined) {
      return ROUTE_PATTERNS.ontology;
    }
    const versionPath = `${ROUTE_PATTERNS.ontology}/${encodePathSegment(location.version)}`;
    return location.termIri ? `${versionPath}/terms/${encodePathSegment(location.termIri)}` : versionPath;
  },
  threads: () => ROUTE_PATTERNS.threads,
  /** The Thread view preparing work: open it through `threads.prepare(intent)`, never directly. */
  threadPreparation: (intentJson: string, requestKey: string) =>
    `${ROUTE_PATTERNS.threadPreparation}?${new URLSearchParams({ [PREPARATION_INTENT_PARAMETER]: intentJson, [PREPARATION_REQUEST_PARAMETER]: requestKey }).toString()}`,
  /** A Thread, and optionally one of its entries as the fragment (`#12`), as cited. */
  thread: (threadId: string, entryNumber: number | null = null) => {
    const threadPath = `${ROUTE_PATTERNS.threads}/${encodePathSegment(threadId)}`;
    return entryNumber === null ? threadPath : `${threadPath}${SEGMENT_SEPARATOR}${String(entryNumber)}`;
  },
  /** A Thread at one of its steps (ADR-0031 D4); `unit` is a review unit to focus, as the review view names it. */
  threadStep: (threadId: string, stepKey: string, unit: string | null = null) => {
    const parameters = new URLSearchParams({ [THREAD_STEP_PARAMETER]: stepKey });
    if (unit !== null) parameters.set("unit", unit);
    return `${ROUTE_PATTERNS.threads}/${encodePathSegment(threadId)}?${parameters.toString()}`;
  },
  /** A Run's live graph (WD-4): its Workflow with each step's state, inputs and outputs, beside its Run view. */
  runGraph: (threadId: string) => `${ROUTE_PATTERNS.threads}/${encodePathSegment(threadId)}/graph`,
  /** The page a host offer's recipient opens to accept or decline it (threads.md T-9). */
  threadHostOffer: (threadId: string, offerId: string) =>
    `${ROUTE_PATTERNS.threads}/${encodePathSegment(threadId)}/host-offers/${encodePathSegment(offerId)}`,
  /** A batch run: one Workflow over many documents (FR-WF-5). */
  batchVita: (batchId: string, vita: string) => `/batches/${encodePathSegment(batchId)}/vitae/${encodePathSegment(vita)}`,
  batch: (batchId: string) => `/batches/${encodePathSegment(batchId)}`,
  /** The process library, on one of its tabs (`?tab=steps`; Workflows by default). */
  workflows: (tab: LibraryTab = "workflows") =>
    tab === "workflows" ? ROUTE_PATTERNS.workflows : `${ROUTE_PATTERNS.workflows}?${new URLSearchParams({ [LIBRARY_TAB_PARAMETER]: tab }).toString()}`,
  /** The step form for a new step (authoring-ui.md AU-4). */
  newStep: () => ROUTE_PATTERNS.newStep,
  /** The artifact form for a new artifact (AU-5). */
  newArtifact: () => ROUTE_PATTERNS.newArtifact,
  newSkill: () => ROUTE_PATTERNS.newSkill,
  newLabelList: () => ROUTE_PATTERNS.newLabelList,
  /** The capability overrides form (capabilities.md CAP-4), optionally with a first entry from its query. */
  newCapabilityOverrides: (query: string | null) =>
    query === null ? ROUTE_PATTERNS.newCapabilityOverrides : `${ROUTE_PATTERNS.newCapabilityOverrides}?${query}`,
  /** One Workflow version (`<item id>@<version>`) in the Workflow view (processes.md P-14). */
  workflow: (reference: string) => `${ROUTE_PATTERNS.workflows}/${encodePathSegment(reference)}`,
  /** The full-page review of one change set. */
  review: (changeSetId: string) => `${ROUTE_PATTERNS.reviews}/${encodePathSegment(changeSetId)}`,
  /** A corpus import a Workflow proposed, for its curator (Q-30). */
  importProposal: (changeSetId: string) => `/import-proposals/${encodePathSegment(changeSetId)}`,
  /** Any project item at one exact version: `<item id>@<version>`. */
  item: (reference: string) => `/items/${encodePathSegment(reference)}`,
  /** "Report a problem", with the reference of the error it is about. */
  issueReport: (reference: string | null) =>
    reference === null
      ? ROUTE_PATTERNS.issueReport
      : `${ROUTE_PATTERNS.issueReport}?${new URLSearchParams({ [ISSUE_REPORT_REFERENCE_PARAMETER]: reference }).toString()}`,
  /** An extraction result, named by the Thread entry that holds its output. */
  extraction: (threadId: string, entry: number) =>
    `${ROUTE_PATTERNS.instances}/${encodePathSegment(threadId)}/${String(entry)}`,
  /** A release series: its versions, `#version-<n>` for one. */
  release: (datasetId: string, number?: number) =>
    `${ROUTE_PATTERNS.formerReleases}/${encodePathSegment(datasetId)}${number === undefined ? "" : `#version-${String(number)}`}`,
} as const;

/** The entry number in a Thread page's fragment (`#12`), or `null`. */
export function entryNumberOfFragment(hash: string): number | null {
  const text = hash.startsWith(SEGMENT_SEPARATOR) ? hash.slice(1) : hash;
  return /^[1-9][0-9]{0,8}$/.test(text) ? Number(text) : null;
}

/** The segment path in a location's fragment (`#subentry.2`), or `null` if there is none. */
export function segmentPathOfFragment(hash: string): string | null {
  const path = decodeURI(hash.startsWith(SEGMENT_SEPARATOR) ? hash.slice(1) : hash);
  return path === "" ? null : path;
}

/** Percent-encode one path segment, but keep `@` readable: it is valid in a path segment. */
function encodePathSegment(text: string): string {
  return encodeURIComponent(text).replaceAll("%40", VERSION_SEPARATOR);
}

/** The sections that were renamed: the old first path segment, and where it lives now. */
const RENAMED_SECTIONS: readonly (readonly [string, string])[] = [
  ["/sources", ROUTE_PATTERNS.sources],
  ["/ontology", ROUTE_PATTERNS.ontology],
  // The Data section's results kept their pages; its list became the review inbox (F11, H-21).
  ["/data/extractions", ROUTE_PATTERNS.instances],
  ["/data", ROUTE_PATTERNS.instances],
];

/** Where an address from before a section's rename now lives, with the rest of its path, its query and its fragment. */
export function pathOfRenamedSection(pathname: string, search: string, hash: string): string {
  const renamed = RENAMED_SECTIONS.find(([old]) => pathname === old || pathname.startsWith(`${old}/`));
  if (renamed === undefined) {
    return `${HOME_PATH}${search}${hash}`;
  }
  const [old, current] = renamed;
  return `${current}${pathname.slice(old.length)}${search}${hash}`;
}
